'use client';

/**
 * ShimmerLoader Component
 *
 * A loading skeleton with animated shimmer effect.
 */

import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';
import { keyframes, type SxProps, styled, type Theme } from '@mui/material/styles';
import type React from 'react';

// ============================================================================
// Types
// ============================================================================

export interface ShimmerLoaderProps {
  /**
   * Loader variant
   * @default 'text'
   */
  variant?: 'text' | 'circular' | 'rectangular' | 'rounded' | 'card' | 'avatar' | 'button';

  /**
   * Width
   */
  width?: number | string;

  /**
   * Height
   */
  height?: number | string;

  /**
   * Number of lines (for text variant)
   * @default 1
   */
  lines?: number;

  /**
   * Enable shimmer animation
   * @default true
   */
  shimmer?: boolean;

  /**
   * Animation speed (ms)
   * @default 1500
   */
  animationSpeed?: number;

  /**
   * Custom className
   */
  className?: string;

  /**
   * MUI sx prop for custom styling
   */
  sx?: SxProps<Theme>;
}

// ============================================================================
// Animations
// ============================================================================

const shimmerAnimation = keyframes`
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
`;

// ============================================================================
// Styled Components
// ============================================================================

interface StyledSkeletonProps {
  $shimmer: boolean;
  $animationSpeed: number;
}

/**
 * StyledSkeleton adds custom animation while letting MUI handle theme colors.
 * By not overriding backgroundColor, we avoid hydration mismatches since
 * MUI's default Skeleton styling is SSR-compatible.
 */
const StyledSkeleton = styled(Skeleton, {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<StyledSkeletonProps>(({ $shimmer, $animationSpeed }) => ({
  borderRadius: 8,
  // Let MUI handle the background color (SSR-safe)
  // Only customize the animation
  ...($shimmer && {
    backgroundSize: '200% 100%',
    animation: `${shimmerAnimation} ${$animationSpeed}ms infinite linear`,
  }),
}));

const CardSkeleton = styled(Box)(({ theme }) => ({
  borderRadius: 16,
  padding: theme.spacing(2),
  // Use transparent background to avoid hydration mismatches
  backgroundColor: 'transparent',
}));

// ============================================================================
// Component
// ============================================================================

export const ShimmerLoader: React.FC<ShimmerLoaderProps> = ({
  variant = 'text',
  width,
  height,
  lines = 1,
  shimmer = true,
  animationSpeed = 1500,
  className,
  sx,
}) => {
  // Preset sizes for common variants
  const presetSizes: Record<string, { width?: number | string; height?: number | string }> = {
    avatar: { width: 48, height: 48 },
    button: { width: 100, height: 40 },
    card: { width: '100%', height: 200 },
  };

  const getSize = (dimension: 'width' | 'height') => {
    if (dimension === 'width' && width) return width;
    if (dimension === 'height' && height) return height;
    return presetSizes[variant]?.[dimension];
  };

  // Render multiple lines for text
  if (variant === 'text' && lines > 1) {
    return (
      <Box className={className} sx={{ display: 'flex', flexDirection: 'column', gap: 1, ...sx }}>
        {Array.from({ length: lines }).map((_, index) => (
          <StyledSkeleton
            key={index}
            variant="text"
            $shimmer={shimmer}
            $animationSpeed={animationSpeed}
            width={index === lines - 1 ? '60%' : getSize('width') || '100%'}
            height={getSize('height')}
          />
        ))}
      </Box>
    );
  }

  // Card variant with internal structure
  if (variant === 'card') {
    return (
      <CardSkeleton className={className}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <StyledSkeleton
            variant="circular"
            $shimmer={shimmer}
            $animationSpeed={animationSpeed}
            width={40}
            height={40}
          />
          <Box sx={{ flex: 1 }}>
            <StyledSkeleton
              variant="text"
              $shimmer={shimmer}
              $animationSpeed={animationSpeed}
              width="50%"
              height={20}
            />
            <StyledSkeleton
              variant="text"
              $shimmer={shimmer}
              $animationSpeed={animationSpeed}
              width="30%"
              height={14}
              sx={{ mt: 0.5 }}
            />
          </Box>
        </Box>
        <StyledSkeleton
          variant="rectangular"
          $shimmer={shimmer}
          $animationSpeed={animationSpeed}
          width="100%"
          height={100}
          sx={{ borderRadius: 2, mb: 2 }}
        />
        <StyledSkeleton variant="text" $shimmer={shimmer} $animationSpeed={animationSpeed} width="100%" height={16} />
        <StyledSkeleton
          variant="text"
          $shimmer={shimmer}
          $animationSpeed={animationSpeed}
          width="80%"
          height={16}
          sx={{ mt: 0.5 }}
        />
      </CardSkeleton>
    );
  }

  // Map variants to MUI skeleton variants
  const skeletonVariant = variant === 'avatar' ? 'circular' : variant === 'button' ? 'rounded' : variant;

  return (
    <StyledSkeleton
      className={className}
      variant={skeletonVariant as 'text' | 'circular' | 'rectangular' | 'rounded'}
      $shimmer={shimmer}
      $animationSpeed={animationSpeed}
      width={getSize('width')}
      height={getSize('height')}
      sx={variant === 'button' ? { borderRadius: '10px', ...sx } : sx}
    />
  );
};

export default ShimmerLoader;
